import { useMemo } from "react";
import { canEditAgent } from "../../../utils/agentRbac";
import { COLORS } from "../../../theme/colors";
import { MATERIALS } from "../../../theme/materials";
import EditableField from "./edit/EditableField";
import { MarkdownText } from "../../../components/MarkdownText";
import { useAvailableModels } from "../../../hooks/useAvailableModels";
import AgentTile from "../iphone-grid/AgentTile";
import SkillsSection from "./SkillsSection";
import QualitySection from "./QualitySection";

// Phase B per agent-detail-viewer-v1.md §10 row 7 +
// agent-experience-redesign-v1.md Phase 4 — Overview is the
// daily-driver tuning surface. Lifted from Graph drawer:
//   - Model & Behavior (provider, model, system_prompt,
//     temperature, max_tokens)
//   - Fact Checker (level, threshold, verify_numbers)
//   - Memory & RAG (long-term memory, conversation window,
//     RAG top_k, score_threshold)
// Tree-shape fields (experts, mode, edges) stay read-only per §5.4.
// Power-user deep-dive editing still available via Graph drawer.

const STATUS_OPTIONS = [
  { value: "live",   label: "Live"   },
  { value: "paused", label: "Paused" },
  { value: "draft",  label: "Draft"  },
];

const FACT_CHECK_LEVELS = [
  { value: "off",      label: "Off"      },
  { value: "standard", label: "Standard" },
  { value: "strict",   label: "Strict"   },
];

const FALLBACK_PROVIDERS = [
  { value: "openai",    label: "openai"    },
  { value: "anthropic", label: "anthropic" },
  { value: "vertex",    label: "vertex"    },
  { value: "bedrock",   label: "bedrock"   },
];

// ── Pure helpers (extracted for unit-testability) ─────────
// BYOK-strict dropdown sources. When the tenant has live provider keys the
// `models` map is populated; otherwise fall back to the static provider list
// so the Provider dropdown still renders (Model dropdown stays disabled).
export function computeProviderOptions(models) {
  const keys = Object.keys(models || {});
  if (keys.length) return keys.map((k) => ({ value: k, label: k }));
  return FALLBACK_PROVIDERS;
}

// Chat-capable models for the selected provider. Uses id, falling back to name.
export function computeModelOptions(models, provider) {
  const list = (models || {})[provider] || [];
  return list
    .filter((m) => m.kind === "chat")
    .map((m) => ({ value: m.id || m.name, label: m.id || m.name }));
}

// sample_questions is a free-form array on metadata_json; coerce non-arrays to [].
export function parseSampleQuestions(metadataJson) {
  return Array.isArray(metadataJson?.sample_questions)
    ? metadataJson.sample_questions
    : [];
}

const Card = ({ title, children, action }) => (
  <div style={{
    padding: 16, borderRadius: 12,
    border: `1px solid ${COLORS.border}`, background: COLORS.surface,
    boxShadow: MATERIALS.spatialShadow(),
  }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
      <div style={{
        fontSize: 11, fontWeight: 700, color: COLORS.textDim,
        textTransform: "uppercase", letterSpacing: "0.06em",
      }}>{title}</div>
      {action}
    </div>
    {children}
  </div>
);

const KV = ({ label, value }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <div style={{ fontSize: 10, color: COLORS.textDim, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</div>
    <div style={{ fontSize: 13, color: COLORS.text }}>{value ?? <span style={{ color: COLORS.textDim }}>—</span>}</div>
  </div>
);

export default function OverviewTab({ tree, onOpenDrawer, saveAgent, currentUser, onSelectAgent, onNavigateWiring }) {
  const agent = tree?.agent || {};
  const experts = tree?.experts || [];
  const isSupervisor = (tree?.agent_kind || "regular") === "supervisor";

  // RBAC per §5.5 — business_user is view-only; everyone else can edit
  // these Overview fields.
  const canEdit = canEditAgent(currentUser?.role);

  // BYOK-strict provider + model dropdowns (Phase 4 lift).
  const { models, llmConfigured } = useAvailableModels();
  const providerOptions = useMemo(() => computeProviderOptions(models), [models]);
  const modelOptions = useMemo(() => computeModelOptions(models, agent.provider), [models, agent.provider]);

  // Save adapters — each builds the minimal partial-patch the
  // backend deep-merges in. saveAgent stages (Phase 3) so these
  // resolve immediately; commit happens via header Save button.
  const saveName            = async (v) => saveAgent({ name: v });
  const saveStatus          = async (v) => saveAgent({ status: v });
  const saveDescription     = async (v) => saveAgent({ metadata_json: { description: v || null } });
  const saveCategory        = async (v) => saveAgent({ metadata_json: { category: (v || "").trim() || null } });
  const saveSampleQuestions = async (v) => saveAgent({
    metadata_json: { sample_questions: Array.isArray(v) ? v : [] },
  });
  // Mirror Builder's dual-write pattern: provider / model / system_prompt
  // / temperature / max_tokens live in BOTH the top-level agent fields
  // (used by Library list views + onboarding) AND `flow_config.llm.*`
  // (read by the BYOK resolver + LangGraph runtime). Writing only one
  // half causes "I changed the model in the Library but the agent still
  // uses the old one" — exactly the Library/Canvas mismatch users
  // reported. The backend deep-merges flow_config so other keys survive.
  const saveProvider        = async (v) =>
    saveAgent({ provider: v, flow_config: { llm: { provider: v } } });
  const saveModel           = async (v) =>
    saveAgent({ model: v, flow_config: { llm: { model: v } } });
  const saveSystemPrompt    = async (v) =>
    saveAgent({ system_prompt: v, flow_config: { llm: { system_prompt: v } } });
  const saveTemperature     = async (v) =>
    saveAgent({ temperature: Number(v), flow_config: { llm: { temperature: Number(v) } } });
  const saveMaxTokens       = async (v) =>
    saveAgent({ max_tokens: Number(v), flow_config: { llm: { max_tokens: Number(v) } } });
  const saveFactCheckerKey  = async (key, val) =>
    saveAgent({ flow_config: { fact_checker: { [key]: val } } });
  const saveMemoryKey       = async (key, val) =>
    saveAgent({ flow_config: { memory: { [key]: val } } });
  const saveKnowledgeKey    = async (key, val) =>
    saveAgent({ flow_config: { knowledge: { [key]: val } } });

  const fc = agent.flow_config || {};
  const fcfg = fc.fact_checker || {};
  const mem = fc.memory || {};
  const kb = fc.knowledge || {};

  const sampleQuestions = parseSampleQuestions(agent.metadata_json);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Identity ────────────────────────────────────────── */}
      <Card title="Identity">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <EditableField
            label="Name"
            variant="text"
            value={agent.name}
            onSave={saveName}
            validate={(v) => (v && v.trim() ? null : "Name is required")}
            readOnly={!canEdit}
          />
          <EditableField
            label="Status"
            variant="pill"
            value={agent.status}
            options={STATUS_OPTIONS}
            onSave={saveStatus}
            readOnly={!canEdit}
          />
          {/* Tree-shape — silent read-only per §5.4. Provider + Model
              live in the editable "Model & Behavior" card below; the
              read-only mirrors here were duplicates that drifted out of
              sync when the user edited from the canonical surface. */}
          <EditableField label="Kind" value={tree?.agent_kind || "regular"} readOnly />
          <EditableField label="Channels" value={(agent.channels || []).join(", ") || "—"} readOnly />
          {/* Category — free-text taxonomy field. Admins type any label
              ("Customer Support", "Internal Tools", …) and it surfaces
              as a filter pill on the Library landing page automatically. */}
          <EditableField
            label="Category"
            variant="text"
            value={agent.metadata_json?.category || ""}
            onSave={saveCategory}
            placeholder="e.g. Customer Support"
            readOnly={!canEdit}
            hint="Groups agents on the Library page. Type any label."
          />
        </div>

        <div style={{ marginTop: 14 }}>
          <EditableField
            label="Description"
            variant="textarea"
            value={agent.metadata_json?.description || ""}
            onSave={saveDescription}
            placeholder="What does this agent do?"
            rows={3}
            readOnly={!canEdit}
          />
        </div>

        {/* Sample questions — list editor (custom variant) ─── */}
        <div style={{ marginTop: 14 }}>
          <EditableField
            label="Sample questions"
            variant="custom"
            value={sampleQuestions}
            onSave={saveSampleQuestions}
            readOnly={!canEdit}
            renderDisplay={(v) => {
              if (!v?.length) return <span style={{ color: COLORS.textDim, fontStyle: "italic" }}>(no sample questions yet)</span>;
              return (
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {v.map((q, i) => (
                    <div key={i} style={{ fontSize: 12, color: COLORS.text }}>{`• ${q}`}</div>
                  ))}
                </div>
              );
            }}
            renderInput={(draft, setDraft, save, revert) => (
              <SampleQuestionsEditor draft={draft || []} setDraft={setDraft} save={save} revert={revert} />
            )}
          />
        </div>
      </Card>

      {/* Skills v2 — on-the-agent skills surface (Overview·Skills mockup).
          Placed high (right under Identity) because it's a primary surface —
          add/preview/remove skills + the auto-attach toggle live here.
          onNavigateWiring switches the viewer to the Wiring tab when a
          skill's "Needs X · Connect" chip is tapped. */}
      {agent.id && (
        <Card title="Skills">
          <SkillsSection
            agentId={agent.id}
            canEdit={canEdit}
            onNavigateWiring={onNavigateWiring}
          />
        </Card>
      )}

      {/* Skills v2 — Quality & Improve (Overview·Quality mockup, screen 7).
          Multi-signal health + honest suggested-improvements empty state. */}
      {agent.id && (
        <Card title="Quality">
          <QualitySection agentId={agent.id} />
        </Card>
      )}

      {/* Phase 4 — Model & Behavior (lifted from Graph drawer call_llm) */}
      <Card title="Model & Behavior">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
          <EditableField
            label="Provider"
            variant="select"
            value={agent.provider || ""}
            options={[{ value: "", label: "(not configured)" }, ...providerOptions]}
            onSave={saveProvider}
            readOnly={!canEdit}
          />
          <EditableField
            label="Model"
            variant="select"
            value={agent.model || ""}
            options={[{ value: "", label: "(pick provider first)" }, ...modelOptions]}
            onSave={saveModel}
            readOnly={!canEdit || !modelOptions.length}
            hint={!llmConfigured ? "No provider configured — set keys in Admin → API Keys" : undefined}
          />
          <EditableField
            label="Temperature"
            variant="slider"
            value={agent.temperature ?? 0.3}
            min={0} max={2} step={0.05}
            onSave={saveTemperature}
            readOnly={!canEdit}
          />
          <EditableField
            label="Max tokens"
            variant="number"
            value={agent.max_tokens ?? 2048}
            min={64} max={32000}
            onSave={saveMaxTokens}
            readOnly={!canEdit}
          />
        </div>
        <div style={{ marginTop: 14 }}>
          <EditableField
            label="System prompt"
            variant="textarea"
            rows={5}
            value={agent.system_prompt || ""}
            onSave={saveSystemPrompt}
            placeholder="System prompt that grounds this agent's behavior…"
            readOnly={!canEdit}
            renderDisplay={(v) => (v && String(v).trim()
              ? <MarkdownText text={String(v)} />
              : <span style={{ color: COLORS.textDim, fontStyle: "italic" }}>(empty)</span>
            )}
          />
        </div>
        <div style={{ fontSize: 10, color: COLORS.textDim, marginTop: 10, lineHeight: 1.5 }}>
          Advanced sampling (top_p, frequency_penalty, presence_penalty) and
          smart-routing tiers live in the Graph tab → call_llm node drawer.
        </div>
      </Card>

      {/* Phase 4 — Fact Checker (lifted from Graph drawer fact_check) */}
      <Card title="Fact Checker">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <EditableField
            label="Level"
            variant="pill"
            value={fcfg.level || "off"}
            options={FACT_CHECK_LEVELS}
            onSave={(v) => saveFactCheckerKey("level", v)}
            readOnly={!canEdit}
            hint="Standard = fast-path · Strict = fast + slow path verification"
          />
          <EditableField
            label="Confidence threshold"
            variant="slider"
            value={fcfg.confidence_threshold ?? 0.7}
            min={0} max={1} step={0.05}
            onSave={(v) => saveFactCheckerKey("confidence_threshold", Number(v))}
            readOnly={!canEdit}
          />
          <EditableField
            label="Verify numbers"
            variant="pill"
            value={fcfg.verify_numbers ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveFactCheckerKey("verify_numbers", v === "yes")}
            readOnly={!canEdit}
          />
          <EditableField
            label="Require sources"
            variant="pill"
            value={fcfg.require_sources ? "yes" : "no"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveFactCheckerKey("require_sources", v === "yes")}
            readOnly={!canEdit}
          />
        </div>
      </Card>

      {/* Phase 4 — Memory & RAG (lifted from Graph drawer parallel_prepare) */}
      <Card title="Memory & RAG">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12 }}>
          <EditableField
            label="Long-term memory"
            variant="pill"
            value={mem.long_term_enabled === false ? "no" : "yes"}
            options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
            onSave={(v) => saveMemoryKey("long_term_enabled", v === "yes")}
            readOnly={!canEdit}
            hint="Customer-profile memories injected into the prompt"
          />
          <EditableField
            label="Conversation window"
            variant="number"
            value={mem.window_size ?? 10}
            min={1} max={100}
            onSave={(v) => saveMemoryKey("window_size", Number(v))}
            readOnly={!canEdit}
          />
          <EditableField
            label="RAG top_k"
            variant="slider"
            value={kb.top_k ?? 8}
            min={1} max={50} step={1}
            onSave={(v) => saveKnowledgeKey("top_k", Number(v))}
            readOnly={!canEdit}
          />
          <EditableField
            label="Score threshold"
            variant="slider"
            value={kb.score_threshold ?? 0.30}
            min={0} max={1} step={0.05}
            onSave={(v) => saveKnowledgeKey("score_threshold", Number(v))}
            readOnly={!canEdit}
          />
        </div>
      </Card>

      {/* Expert tree (Supervisor only) — same iPhone-grid tile look as
          the Library so experts visually live "inside" their supervisor.
          Tile click navigates to the expert's Detail Viewer (same flow
          as Library tile click) via onSelectAgent threaded down from
          AgentLibraryView. Falls back to onOpenDrawer for the legacy
          drawer path if onSelectAgent isn't provided.

          Hidden when no experts exist OR when onSelectAgent isn't wired
          (e.g., embedded contexts where navigation isn't available),
          falling back to a simple "no experts yet" message. */}
      {isSupervisor && (
        <Card title={`Experts (${experts.length})`}>
          {experts.length === 0 ? (
            <div style={{ fontSize: 12, color: COLORS.textDim }}>
              This Supervisor has no expert agents yet. Use the Builder to add experts.
            </div>
          ) : (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: 4,
              padding: "8px 4px",
            }}>
              {experts.map((e) => (
                <AgentTile
                  key={e.id}
                  agent={e}
                  density="compact"
                  onClick={(a) => {
                    if (onSelectAgent) {
                      onSelectAgent(a.id);
                      return;
                    }
                    // Legacy fallback for embedded contexts without
                    // navigation handler — opens the inspector drawer
                    // with sibling-cycling so the user can still see
                    // expert details inline.
                    if (onOpenDrawer) {
                      const siblings = experts.map(x => ({
                        kind: `Expert: ${x.name}`, payload: x,
                      }));
                      const idx = experts.findIndex(x => x.id === a.id);
                      onOpenDrawer({
                        kind: `Expert: ${a.name}`, payload: a,
                        siblings, index: idx >= 0 ? idx : 0,
                      });
                    }
                  }}
                />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Baseline footer ─────────────────────────────────── */}
      {tree?.template_baseline && (
        <Card title="Marketplace baseline">
          <div style={{ fontSize: 11, color: COLORS.textMuted, fontFamily: "monospace", lineHeight: 1.6 }}>
            <div>template_id: {tree.template_baseline.template_id}</div>
            <div>schema_version: {tree.template_baseline.schema_version}</div>
            <div>installed_at: {tree.template_baseline.installed_at}</div>
            <div>baseline_hash: {tree.template_baseline.snapshot_hash}</div>
            {tree.current_hash && tree.current_hash !== tree.template_baseline.snapshot_hash && (
              <div style={{ color: COLORS.orange }}>
                current_hash: {tree.current_hash} ⟶ update available
              </div>
            )}
          </div>
        </Card>
      )}

    </div>
  );
}

// B1 — sample_questions list editor (custom EditableField variant).
// Add / remove / reorder strings; Save commits the whole array;
// Cancel reverts to the persisted value.
export function SampleQuestionsEditor({ draft, setDraft, save, revert }) {
  const list = Array.isArray(draft) ? draft : [];
  const setAt = (i, v) => setDraft(list.map((x, j) => (j === i ? v : x)));
  const removeAt = (i) => setDraft(list.filter((_, j) => j !== i));
  const moveUp = (i) => {
    if (i === 0) return;
    const next = [...list];
    [next[i - 1], next[i]] = [next[i], next[i - 1]];
    setDraft(next);
  };
  const moveDown = (i) => {
    if (i >= list.length - 1) return;
    const next = [...list];
    [next[i], next[i + 1]] = [next[i + 1], next[i]];
    setDraft(next);
  };
  const add = () => setDraft([...list, ""]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {list.map((q, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <input
            type="text"
            value={q}
            onChange={(e) => setAt(i, e.target.value)}
            placeholder="Sample question…"
            style={{
              flex: 1,
              fontSize: 12, color: COLORS.text,
              background: COLORS.surface,
              border: `1px solid ${COLORS.border}`, borderRadius: 5,
              padding: "5px 8px", outline: "none",
            }}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); revert(); }
              else if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); save(); }
            }}
          />
          <button type="button" title="Move up" onClick={() => moveUp(i)} style={miniBtn}>{"↑"}</button>
          <button type="button" title="Move down" onClick={() => moveDown(i)} style={miniBtn}>{"↓"}</button>
          <button type="button" title="Remove" onClick={() => removeAt(i)} style={{ ...miniBtn, color: COLORS.pink }}>{"×"}</button>
        </div>
      ))}
      <div style={{ display: "flex", gap: 6, marginTop: 4 }}>
        <button type="button" onClick={add} style={{
          background: "transparent", border: `1px dashed ${COLORS.border}`,
          color: COLORS.textMuted, padding: "4px 10px", borderRadius: 5,
          fontSize: 11, cursor: "pointer",
        }}>{"+ Add question"}</button>
        <button type="button" onClick={save} style={{
          background: COLORS.accent, color: "#fff", border: "none",
          padding: "4px 10px", borderRadius: 5, fontSize: 11, fontWeight: 600,
          cursor: "pointer",
        }}>Save</button>
        <button type="button" onClick={revert} style={{
          background: "transparent", border: `1px solid ${COLORS.border}`,
          color: COLORS.textDim, padding: "4px 10px", borderRadius: 5,
          fontSize: 11, cursor: "pointer",
        }}>Cancel</button>
      </div>
    </div>
  );
}

const miniBtn = {
  background: "transparent", border: `1px solid ${COLORS.border}`,
  color: COLORS.textDim, width: 22, height: 22, borderRadius: 4,
  fontSize: 11, cursor: "pointer", lineHeight: 1,
};
